import React from "react";
import { Mutation, Query } from "react-apollo";
import ReCAPTCHA from "react-google-recaptcha";
import Button from "@streeteasy/design-system/deprecated/Button";
import AppleIcon from "@streeteasy/design-system/deprecated/Icons/SVG/AppleIcon";
import GoogleIcon from "@streeteasy/design-system/deprecated/Icons/SVG/GoogleIcon";
import Text from "@streeteasy/design-system/deprecated/Text";
import TextLink from "@streeteasy/design-system/deprecated/TextLink";
import XButton from "@streeteasy/design-system/deprecated/XButton";
import { camelCase, mapKeys } from "lodash";
import { bool, func, instanceOf, string } from "prop-types";

import GlobalState from "../../../../../../../shared/utilities/globalState";
import { emailCaptureGA } from "../../../../analytics/ga";
import FREEPASS_LOGIN from "../../../../mutations/freepass_login";
import SHOW_RECAPTCHA from "../../../../queries/show_recaptcha";
import SmartEmailField from "../../../SmartEmailField";
import {
  ErrorText,
  MobileLineBrake,
  ModalButton,
  ModalDisclaimer,
  ModalErrorBox,
  ModalHeading,
  ModalInput,
  ModalRecaptcha,
  ModalRecaptchaWrapper,
  ModalText,
  ModalTitle,
  PaddedText,
  SignInWrapper,
  SplittedDivider,
  TextCopy,
  XButtonWrapper
} from "../../styled";

class EmailCapture extends React.Component {
  static defaultProps = {
    form: {},
    formError: "",
    handleCloseModal: () => {},
    handleInputChange: () => {},
    handleMutationError: () => {},
    handleRenderError: () => {},
    setScenario: () => {},
    showGoogle: false,
    siteKey: ""
  };

  static propTypes = {
    form: instanceOf(Object),
    formError: string,
    handleCloseModal: func,
    handleCreateBoundary: func.isRequired,
    handleInputChange: func,
    handleMutationError: func,
    handleRedirectTo: func.isRequired,
    handleRenderError: func,
    handleSuggestedEmailClick: func.isRequired,
    setScenario: func,
    showGoogle: bool,
    siteKey: string
  };

  state = {
    disabledButton: false,
    itemType: "",
    isRecaptchaValid: true,
    showRecaptcha: false
  };

  componentDidMount() {
    // GA starts
    if (GlobalState.get("analyticsData")) {
      this.source = GlobalState.get("analyticsData").source || null;
    } else if (localStorage.getItem("analytics")) {
      this.source =
        JSON.parse(localStorage.getItem("analytics")).source || null;
    }

    localStorage.setItem("analytics", JSON.stringify({ source: this.source }));
    emailCaptureGA.signInOpenModal(this.source);
    // GA ends

    if (this.source === "save" && GlobalState.has("itemData")) {
      this.setSavedItemType();
    }
  }

  setSavedItemType = () => {
    const { itemType } = GlobalState.get("itemData");

    this.setState({
      itemType: ["Sale", "Rental"].includes(itemType)
        ? "listing"
        : itemType.toLowerCase()
    });
  };

  handleQueryCompleted = data => {
    this.setState({
      showRecaptcha: data.show_recaptcha
    });
  };

  handleQueryErrored = () => {
    this.setState({
      showRecaptcha: true
    });
  };

  handleMutationCompleted = data => {
    const loginData = data.freepass_login;

    // GA starts
    const analyticsData = mapKeys(loginData.analytics_data, (value, key) =>
      camelCase(key)
    );
    analyticsData.source = this.source;
    localStorage.setItem("analytics", JSON.stringify(analyticsData));
    // GA ends

    if (
      typeof this.props.form.data.boundary === "object" &&
      loginData.__typename === "AccountCreated"
    ) {
      GlobalState.registerCallback(
        "accountCreated",
        this.props.handleCreateBoundary
      );
    }

    if (loginData.__typename === "AccountCreated") {
      // GA starts
      emailCaptureGA.accountIsCreated(this.source, analyticsData.flow);
      // GA ends

      if (GlobalState.hasCallbacks("accountCreated")) {
        GlobalState.runCallbacks("accountCreated");
        this.props.handleCloseModal();
      } else {
        this.props.handleRedirectTo(this.props.form.data.redirectTo);
      }
    } else if (loginData.__typename === "SSOUser") {
      window.location.href = loginData.url;
    }

    // GA starts
    emailCaptureGA.signInSubmitForm(this.source);
    // GA ends

    this.props.setScenario(loginData.__typename, {
      origin:
        typeof this.source === "string" ? this.source : this.props.form.origin
    });
  };

  handleSubmit = mutationQuery => {
    const recaptchaResponse = this.state.showRecaptcha
      ? this.recaptchaRef.current.getValue()
      : "";

    const mutationData = {
      variables: {
        data: JSON.stringify(this.props.form.data),
        email: this.props.form.email,
        recaptcha_token: recaptchaResponse,
        origin:
          typeof this.source === "string" ? this.source : this.props.form.origin
      }
    };

    mutationQuery(mutationData);

    this.setState({ disabledButton: true });
  };

  handleError = response => {
    const isRecaptchaError = response.graphQLErrors.some(
      error => error.type === "recaptcha_failed"
    );

    const isAnyError = response.graphQLErrors.some(
      error => error.type !== "recaptcha_failed"
    );

    if (isRecaptchaError) {
      this.setState({
        isRecaptchaValid: false
      });
    }

    if (this.state.showRecaptcha && isAnyError) {
      this.recaptchaRef.current.reset();
    }

    if (isAnyError) {
      this.props.handleMutationError(response);
    }

    this.setState({ disabledButton: false });
  };

  handleSignInButtonClick = type => {
    emailCaptureGA.signInButtonClick(type);
  };

  handleRecaptchaChange = () => {
    const recaptchaResponse = this.recaptchaRef.current.getValue();

    if (recaptchaResponse) {
      this.setState({ isRecaptchaValid: true });
    }
  };

  handleRecaptchaErrorMessage = () => {
    if (!this.state.isRecaptchaValid) {
      return (
        <ModalErrorBox>
          <ErrorText role="alert" type="sm">
            Cannot log in because recaptcha couldn’t be verified
          </ErrorText>
        </ModalErrorBox>
      );
    }

    return false;
  };

  recaptchaRef = React.createRef();

  render() {
    return (
      <div>
        <ModalHeading>
          <ModalTitle type="primarySm">Sign In / Register</ModalTitle>
          <XButtonWrapper>
            <XButton onClick={this.props.handleCloseModal} />
          </XButtonWrapper>
        </ModalHeading>
        <ModalText>
          <Text type="sm">
            {this.state.itemType
              ? `Finish saving this ${this.state.itemType} to get updates`
              : "Take full advantage of StreetEasy’s features"}
          </Text>
        </ModalText>
        <Mutation
          mutation={FREEPASS_LOGIN}
          onCompleted={data => this.handleMutationCompleted(data)}
          onError={response => this.handleError(response)}
        >
          {mutationQuery => (
            <form
              onSubmit={e => {
                this.handleSubmit(mutationQuery);
                e.preventDefault();
              }}
            >
              <ModalInput>
                <SmartEmailField
                  handleInputChange={this.props.handleInputChange}
                  handleSuggestedEmailClick={
                    this.props.handleSuggestedEmailClick
                  }
                  isInvalid={this.props.formError}
                  value={this.props.form.email}
                />
              </ModalInput>
              {this.props.handleRenderError()}

              <ModalDisclaimer>
                <Text type="disclaimer">
                  Real estate professional? <MobileLineBrake />
                  Use your company email address.
                </Text>
              </ModalDisclaimer>
              <Query
                onCompleted={data => this.handleQueryCompleted(data)}
                onError={() => this.handleQueryErrored}
                query={SHOW_RECAPTCHA}
              >
                {() =>
                  this.state.showRecaptcha && (
                    <ModalRecaptcha>
                      <ModalRecaptchaWrapper>
                        <ReCAPTCHA
                          ref={this.recaptchaRef}
                          onChange={this.handleRecaptchaChange}
                          sitekey={this.props.siteKey}
                        />
                      </ModalRecaptchaWrapper>
                      {this.handleRecaptchaErrorMessage()}
                    </ModalRecaptcha>
                  )
                }
              </Query>
              <ModalButton>
                <Button
                  disabled={this.state.disabledButton}
                  theme="primary"
                  type="submit"
                >
                  Submit
                </Button>
              </ModalButton>
            </form>
          )}
        </Mutation>
        <SplittedDivider>
          <PaddedText>
            <Text type="disclaimer">or</Text>
          </PaddedText>
        </SplittedDivider>
        <SignInWrapper>
          <Button
            elementType="link"
            href={`/users/auth/apple/?origin=${window.location.href}`}
            minWidth
            onClick={() => this.handleSignInButtonClick("apple")}
            theme="apple"
          >
            <AppleIcon />
            Continue with Apple
          </Button>
        </SignInWrapper>
        {this.props.showGoogle && (
          <SignInWrapper>
            <Button
              elementType="link"
              href={`/users/auth/google_oauth2/?origin=${window.location.href}`}
              minWidth
              onClick={() => this.handleSignInButtonClick("google")}
              theme="google"
            >
              <GoogleIcon />
              Continue with Google
            </Button>
          </SignInWrapper>
        )}
        <TextCopy type="sm">
          I accept StreetEasy&apos;s&nbsp;
          <TextLink
            href="https://www.zillow.com/corp/Terms.htm"
            target="_blank"
            textStyle="sm"
            theme="primaryTouchedUnderline"
          >
            Terms of Use
          </TextLink>
        </TextCopy>
      </div>
    );
  }
}

export default EmailCapture;
